import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Account creation and deletion instructions for Instant Guide (Software Beyond Limits).
 * This page satisfies Google Play's requirement for a link that explains how users
 * can create an account and request account/data deletion.
 */
function InstantGuideAccountData() {
  return (
    <div className="privacy-page">
      <header className="privacy-header">
        <Link to="/apps/instant-guide" className="privacy-back">
          ← Instant Guide: Software Beyond Limits
        </Link>
      </header>
      <main className="privacy-content">
        <h1>Instant Guide: Account Policy</h1>
        <p className="privacy-updated">
          How to create an account and how to request deletion of your account and data.
        </p>

        <section>
          <h2>Creating an account</h2>
          <p>
            <strong>Instant Guide</strong> (by Software Beyond Limits) lets you use the app without an account.
            You can create an account at any time to:
          </p>
          <ul>
            <li>Sync your unlocked tours and progress across devices</li>
            <li>Restore in-app purchases on a new device</li>
            <li>Save your language and preferences</li>
          </ul>
          <p>
            <strong>To create an account:</strong> Open the app → go to the <strong>Profile</strong> tab →
            tap <strong>Sign in</strong> → choose <strong>Sign in with Google</strong> or <strong>Sign in with Apple</strong>.
            Your account is created when you complete sign-in.
          </p>
        </section>

        <section>
          <h2>Requesting account and data deletion</h2>
          <p>
            You can delete your Instant Guide account and request that we delete your data at any time.
          </p>
          <ol className="steps-list">
            <li>
              Open the <strong>Instant Guide</strong> app on your device.
            </li>
            <li>
              Go to the <strong>Profile</strong> tab (person icon in the bottom navigation).
            </li>
            <li>
              Tap <strong>Delete account</strong>.
            </li>
            <li>
              Confirm that you want to delete your account and all associated data.
            </li>
          </ol>
          <p>
            After you confirm, your account and the data we hold about you are deleted as described below.
            This action cannot be undone.
          </p>
        </section>

        <section>
          <h2>Data we delete</h2>
          <p>When you delete your account, we delete:</p>
          <ul>
            <li>Your account record (email, display name, profile photo, sign-in provider)</li>
            <li>Your credits and purchase/unlock history</li>
            <li>Your saved and unlocked tours list</li>
            <li>Your language preference and app preferences</li>
          </ul>
        </section>

        <section>
          <h2>Data we may keep</h2>
          <p>We may retain the following only as long as required by law or for legitimate operational needs:</p>
          <ul>
            <li>
              <strong>Purchase records:</strong> Anonymous or minimal transaction records may be kept for
              tax, fraud prevention, or app-store requirements (typically no longer than required by law,
              e.g. several years).
            </li>
          </ul>
        </section>

        <section>
          <h2>If you cannot use the in-app option</h2>
          <p>
            If you are unable to open the app or use the in-app delete option, you can request account
            and data deletion by email. Contact us at{' '}
            <a href="mailto:softwarebeyondlimits@gmail.com">softwarebeyondlimits@gmail.com</a> with the
            subject line &ldquo;Instant Guide – Delete my account&rdquo; and the email address associated
            with your Instant Guide account. We will process your request and confirm once your account
            and data have been deleted.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            For any questions about your account or data, contact Software Beyond Limits at{' '}
            <a href="mailto:softwarebeyondlimits@gmail.com">softwarebeyondlimits@gmail.com</a>.
          </p>
        </section>
      </main>
    </div>
  );
}

export default InstantGuideAccountData;
